// 2026-07-21 - FormuLearning - 제외 사유·유형 입력 모달 (이슈 #43)
// 2026-08-30 수정 - 이슈 #58 S1(#64): 대상을 배치에서 **배치·Lot 공용**으로 넓혔다.
//                   Lot 제외(BR-LOT-14)도 유형 4종이 같아 모달을 따로 둘 이유가 없다.
// 2026-09-24 수정 - 이슈 #75(G4): Lot 제외 폐지(`M901-18`) — 다시 **배치 전용**이다.
// 파일 위치: frontend/src/features/step4/ExcludeDialog.tsx
// 목적: BR-S4-L03 — 제외 시 유형(4종)을 필수로, 사유를 선택으로 받는다.
//       복원은 가역이므로 이 모달을 거치지 않는다(1클릭).

import { useState } from 'react'
import { EXCLUDE_TYPES, EXCLUDE_TYPE_LABELS, type ExcludeType } from '../../api/lot'

interface Props {
  /** 제외 대상 표시명 — 배치는 `batch_code`, Lot 은 표시명(`lotDisplayName`). */
  targetLabel: string
  /** 모달 헤더. 기본은 배치 제외. */
  title?: string
  onConfirm: (type: ExcludeType, reason: string) => Promise<void>
  onCancel: () => void
}

export default function ExcludeDialog({ targetLabel, title = 'Exclude Batch', onConfirm, onCancel }: Props) {
  const [type, setType] = useState<ExcludeType | null>(null)
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)

  const handleConfirm = async () => {
    if (!type) return
    setSaving(true)
    try {
      await onConfirm(type, reason.trim())
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onCancel}>
      <div
        className="w-80 rounded p-4"
        style={{ backgroundColor: 'var(--color-bg-panel)', border: '1px solid var(--border-soft)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-text-secondary">
          {title}
        </h4>
        <p className="mb-3 text-[11px] text-text-body">
          <span className="font-medium">{targetLabel}</span>를 계산에서 제외합니다. 저장된 결과는
          삭제되지 않으며 언제든 복원할 수 있습니다.
        </p>

        <p className="mb-1.5 text-[10px] font-medium text-text-secondary">
          제외 유형 <span className="text-danger">*</span>
        </p>
        <div className="mb-3 flex flex-col gap-1">
          {EXCLUDE_TYPES.map((t) => (
            <label key={t} className="flex cursor-pointer items-center gap-2 text-[11px] text-text-body">
              <input
                type="radio"
                name="exclude-type"
                checked={type === t}
                onChange={() => setType(t)}
                className="accent-[var(--color-accent-teal)]"
              />
              {EXCLUDE_TYPE_LABELS[t]}
            </label>
          ))}
        </div>

        <p className="mb-1.5 text-[10px] font-medium text-text-secondary">사유 (선택)</p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="예: 12시간 시점 용출 측정 중 교반 정지"
          className="mb-3 w-full resize-none rounded bg-bg-input px-2 py-1.5 text-[11px] text-text-body outline-none"
          style={{ border: '1px solid var(--border-soft)' }}
        />

        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded px-3 py-1.5 text-[11px] text-text-secondary transition-colors hover:bg-bg-input"
            style={{ border: '1px solid var(--border-subtle)' }}
          >
            취소
          </button>
          <button
            onClick={handleConfirm}
            disabled={!type || saving}
            className="rounded bg-accent-teal px-3 py-1.5 text-[11px] font-semibold text-[#1a1b1e] transition-opacity hover:opacity-90 disabled:opacity-40"
          >
            {saving ? '처리 중...' : '제외'}
          </button>
        </div>
      </div>
    </div>
  )
}
